import React, { useEffect } from 'react'

// Third party
import clsx from 'clsx'
import { v4 } from 'uuid'
import { Tooltip } from '@mui/material'

import TooltipIcon from '../../../../helpers/TooltipIcon'

import { formatDateWithTime } from '../../../../helpers/time'

import { IRegistrationTicketInterface } from '../../../../models/registration-tickets/registrationTicket.interface'
import { IPaymentStatus, PAYMENT_STATUS } from '../../../../types/payment'
import { getFloatPrice } from '../../../../helpers/price'

// Types
type IExhibitorTicketsAccordion = {
  data: IRegistrationTicketInterface[]
  showScratchedView: boolean
  setListToScratch: React.Dispatch<React.SetStateAction<string[]>>
  listToScratch: string[]
  registrationticketData: IRegistrationTicketInterface[]
}

type IDetailRowProps = {
  title: string | null
  date: string | null
  cost?: number
  units: number
  itemId?: string | null
  showScratchedView: boolean
  listToScratch: string[]
  handleScratchList: (itemId: string) => void
  isScratched?: boolean
  Item: IRegistrationTicketInterface
  paymentStatus: IPaymentStatus
}

const ExhibitorTicketsInput: React.FC<{
  className?: string
  data?: any
  icon?: any
}> = ({ className, data, icon }) => {
  return (
    <>
      {icon ? (
        <div className={`${className}`}>
          <img className="absolute left-3 top-3" src={icon} alt="icons" />
          {data}
        </div>
      ) : (
        <div className={`${className}`}>{data}</div>
      )}
    </>
  )
}

const DetailRow = ({
  title,
  cost,
  units,
  showScratchedView,
  isScratched,
  Item,
  listToScratch,
  itemId,
  handleScratchList,
  paymentStatus,
  date,
}: IDetailRowProps) => {
  let id = `${v4()}${Date.now()}`
  let checked = Item.id ? listToScratch.find((data) => data === Item.id) : false

  // { title, cost, totalFees, units }: { title: any, cost?: any, totalFees?: any, units: any }
  return (
    <div className="border rounded-md shadow-sm border-[#D3DAEE] lg:border-0 lg:shadow-none p-2 lg:p-0 mt-4 lg:mt-0 flex flex-col lg:flex-row lg:items-center w-full">
      <div className="lg:hidden font-medium text-[12px]">Ticket</div>
      <ExhibitorTicketsInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4"
        data={title}
      />
      <div className="lg:hidden font-medium text-[12px]">Date</div>

      <ExhibitorTicketsInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4"
        data={date}
      />
      <div className="lg:hidden mt-2 font-medium text-[12px]">Amount</div>
      <ExhibitorTicketsInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 lg:text-center"
        data={units}
      />
      <div className="lg:hidden mt-2 font-medium text-[12px]">Price per ticket</div>
      <ExhibitorTicketsInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 lg:text-center"
        data={getFloatPrice(`${cost}`)}
      />
      <div className="lg:hidden mt-2 font-medium text-[12px]">Total</div>
      <ExhibitorTicketsInput
        className="relative rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 lg:m-1 w-full lg:w-1/4 text-center"
        data={isScratched ? 'Scratched' : `$${Number((cost ?? 0) * units).toFixed(2)}`}
        icon={
          paymentStatus === PAYMENT_STATUS.PAID
            ? '/assets/img/dark/credit-card.svg'
            : '/assets/img/dark/cash.svg'
        }
      />

      {showScratchedView && !!itemId ? (
        <label
          htmlFor={id}
          className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor h-12 bg-SeabiscuitGrayThemeColor p-3 m-1 w-1/4 text-center relative flex items-center justify-center"
        >
          {isScratched ? (
            'Scratched'
          ) : (
            <>
              {' '}
              Scratch
              <div
                className={clsx(
                  'ml-2 !ring-0 !shadow-none absolute shrink-0 flex items-center justify-center',
                  !checked
                    ? 'border-2 border-SeabiscuitDark200ThemeColor rounded-full w-5 h-5 right-[14px]'
                    : 'w-6 h-6 right-3'
                )}
              >
                {checked || isScratched ? (
                  <img
                    src="/assets/og_icons/Ok-3.svg"
                    alt="Ok"
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>{' '}
            </>
          )}

          {isScratched ? null : (
            <input
              type="checkbox"
              className="hidden"
              id={id}
              checked={!!checked}
              onChange={() => handleScratchList(itemId as string)}
            />
          )}
        </label>
      ) : null}
    </div>
  )
}

const ExhibitorTicketsAccordion = (props: IExhibitorTicketsAccordion) => {
  // Hooks and vars
  const { showScratchedView, setListToScratch, listToScratch, registrationticketData } = props
  const data = registrationticketData

  useEffect(() => {
    setListToScratch([])
  }, [showScratchedView])

  // Functions
  const handleScratchList = (itemId: string) => {
    if (listToScratch.find((data) => data === itemId)) {
      let filter = listToScratch.filter((data) => data !== itemId)
      setListToScratch(filter)
    } else {
      setListToScratch([...listToScratch, itemId])
    }
  }

  return (
    <>
      <div className="hidden lg:flex items-center w-full">
        <ExhibitorTicketsInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Ticket</span>}
        />
        <ExhibitorTicketsInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Date</span>}
        />
        <ExhibitorTicketsInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Amount</span>}
        />
        <ExhibitorTicketsInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Price per ticket</span>}
        />
        <ExhibitorTicketsInput
          className={clsx(
            'text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold',
            showScratchedView ? 'w-1/5' : 'w-1/4'
          )}
          data={<span className="flex items-center">Total</span>}
        />

        {showScratchedView ? (
          <ExhibitorTicketsInput
            className={clsx(
              'xl:text-sm 2xl:text-base text-SeabiscuitDark200ThemeColor ml-2 font-semibold w-1/5'
            )}
            data={
              <span className="flex items-center">
                Scratch
                <Tooltip title={<h1 className="tooltip_title">Scratch</h1>} placement="top" arrow>
                  <button className="">
                    <TooltipIcon color="#122B46" />
                  </button>
                </Tooltip>
              </span>
            }
          />
        ) : null}
      </div>

      {data && data.length
        ? data.map((currentItem, index) => {
            return (
              <DetailRow
                key={`${JSON.stringify(currentItem)}${index}`}
                title={currentItem.ticketTitle}
                units={currentItem.selectedUnitsCount ?? 0}
                cost={currentItem.ticketPrice ?? 0}
                date={`${currentItem.created ? formatDateWithTime(currentItem.created) : ''}`}
                itemId={currentItem?.id}
                listToScratch={listToScratch}
                Item={currentItem}
                showScratchedView={showScratchedView}
                handleScratchList={handleScratchList}
                isScratched={currentItem?.isScratched ?? false}
                paymentStatus={currentItem?.paymentStatus ?? null}
              />
            )
          })
        : null}
    </>
  )
}

export default ExhibitorTicketsAccordion
